import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  debounceTime,
  fromEvent,
  merge,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  timer,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { validateAccept } from '@imagise/shared';

@Directive({
  selector: '[libFileDrop]',
  exportAs: 'libFileDrop',
  host: {
    '(dragover)': 'handleDragOver($event)',
    '(drop)': 'handleDrop($event)',
  },
})
export class FileDropDirective implements OnInit {
  private readonly renderer = inject(Renderer2);

  private readonly el = inject(ElementRef);

  private readonly destroyRef = inject(DestroyRef);

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly accept = input<string | string[] | null>(null);

  readonly filesDropped = output<FileList>();

  readonly isDraggingFileChanged = output<boolean>();

  readonly draggedIn = output<void>();

  readonly draggedOut = output<void>();

  // Internal mirrors of draggedIn/draggedOut: outputs are not observable,
  // while the dragleave stream below needs them as an unsubscribe signal.
  private readonly draggedIn$ = new Subject<void>();

  private readonly draggedOut$ = new Subject<void>();

  protected handleDragOver(event: DragEvent): void {
    if (!event.target || !event.currentTarget) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isDraggingFileChanged.emit(true);
    this.draggedIn$.next();
    this.draggedIn.emit();

    this.renderer.addClass(this.el.nativeElement, 'drag-over');

    const unsubscribe$ = merge(this.draggedIn$, this.draggedOut$);

    fromEvent<DragEvent>(event.currentTarget, 'dragleave')
      .pipe(debounceTime(100), takeUntil(unsubscribe$), takeUntilDestroyed(this.destroyRef))
      .subscribe((dragleaveEvent) => {
        dragleaveEvent.stopPropagation();
        dragleaveEvent.preventDefault();

        this.isDraggingFileChanged.emit(false);
        this.draggedOut$.next();
        this.draggedOut.emit();

        this.renderer.removeClass(this.el.nativeElement, 'drag-over');
      });
  }

  protected handleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files
      ? this.filterAcceptFiles(event.dataTransfer.files)
      : null;

    if (files && files.length > 0) {
      this.filesDropped.emit(files);
    }

    this.isDraggingFileChanged.emit(false);
    this.draggedOut$.next();
    this.draggedOut.emit();

    this.renderer.removeClass(this.el.nativeElement, 'drag-over');
    this.renderer.addClass(this.el.nativeElement, 'file-dropped');

    timer(600)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.renderer.removeClass(this.el.nativeElement, 'file-dropped');
      });
  }

  public ngOnInit(): void {
    // Document-level drag listeners only make sense in the browser; during SSR
    // prerender there is no document at all.
    if (!this.isBrowser) {
      return;
    }

    const inDocument$ = merge(
      fromEvent(document, 'dragover', { capture: true }),
      fromEvent(document, 'dragenter', { capture: true })
    ).pipe(
      tap(() => {
        this.renderer.addClass(this.el.nativeElement, 'file-drop-area');
      })
    );

    const outDocument$ = merge(
      fromEvent(document, 'dragend', { capture: true }),
      fromEvent(document, 'mouseleave', { capture: true }),
      fromEvent(document, 'drop', { capture: true })
    ).pipe(
      throttleTime(600),
      switchMap(() => timer(600)),
      tap(() => {
        this.renderer.removeClass(this.el.nativeElement, 'file-drop-area');
      })
    );

    inDocument$
      .pipe(
        switchMap(() => outDocument$.pipe(takeUntil(inDocument$))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected filterAcceptFiles(files: FileList): FileList {
    const validFiles = new DataTransfer();

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (file && validateAccept(file, this.accept())) {
        validFiles.items.add(file);
      }
    }

    return validFiles.files;
  }
}
